import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../ui/Card/Card';
import Button from '../ui/Button/Button';
import Input from '../ui/Input/Input';
import Checkbox from '../ui/Checkbox/Checkbox';
import Popover from '../ui/Popover/Popover';
import { createUserAccessRole, updateUserAccessRole, getUserAccessRoles, toggleUserAccessRoleArchive, getUsers, getUserIncubationAccess, getAllFunctionalities } from '../../controllers/users';
import { getOrganizations } from '../../controllers/organizations';
import { useToast } from '../../contexts/ToastContext';
import './DashboardAccess.css';
import Skeleton from '../ui/Skeleton/Skeleton';

// Sample data for Dashboard Users
const sampleDashboardUsers = [
  { name: 'Sarah Johnson', organization: 'PetCare Inc.', email: 'sarah.johnson@petcare.com', role: 'Admin', incubations: 'All' },
  { name: 'Michael Chen', organization: 'VetTech Solutions', email: 'michael.chen@vettech.com', role: 'CSR', incubations: 'Birthday Challenge' },
  { name: 'Emily Rodriguez', organization: 'Animal Wellness Co.', email: 'emily.rodriguez@animalwellness.com', role: 'Viewer', incubations: 'Perfect Poop' },
  { name: 'David Kim', organization: 'Pet Health Labs', email: 'david.kim@pethealthlabs.com', role: 'Admin', incubations: 'All' },
  { name: 'Lisa Thompson', organization: 'Furry Friends Clinic', email: 'lisa.thompson@furryfriends.com', role: 'CSR', incubations: 'PetFit' },
  { name: 'James Wilson', organization: 'PetCare Inc.', email: 'james.wilson@petcare.com', role: 'Viewer', incubations: 'Know Your Pet' },
  { name: 'Anna Martinez', organization: 'VetTech Solutions', email: 'anna.martinez@vettech.com', role: 'Admin', incubations: 'All' },
  { name: 'Robert Brown', organization: 'Animal Wellness Co.', email: 'robert.brown@animalwellness.com', role: 'CSR', incubations: 'Healthy Habits' },
  { name: 'Jennifer Lee', organization: 'PetCare Inc.', email: 'jennifer.lee@petcare.com', role: 'Viewer', incubations: 'Pet Nutrition' },
  { name: 'Thomas Garcia', organization: 'VetTech Solutions', email: 'thomas.garcia@vettech.com', role: 'CSR', incubations: 'Daily Exercise' },
  { name: 'Rachel White', organization: 'Animal Wellness Co.', email: 'rachel.white@animalwellness.com', role: 'Admin', incubations: 'All' },
  { name: 'Christopher Davis', organization: 'Pet Health Labs', email: 'christopher.davis@pethealthlabs.com', role: 'Viewer', incubations: 'Sleep Training' },
];

// Generic function to convert keys to display text
const formatDisplayText = (text) => {
  if (!text) return '';
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const DashboardAccess = () => {
  const navigate = useNavigate();
  const { showError, showSuccess, showWarning } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userAccessRoles, setUserAccessRoles] = useState([]);
  const [archivedRoles, setArchivedRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [archivingRoleId, setArchivingRoleId] = useState(null);
  const [incubationFunctionalities, setIncubationFunctionalities] = useState([]);
  const [roleModal, setRoleModal] = useState(null);
  const [savingRole, setSavingRole] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userIncubationCounts, setUserIncubationCounts] = useState({});
  const [usersPagination, setUsersPagination] = useState({
    total: 0,
    total_pages: 1,
    current_page: 1,
    limit: 8,
    has_next: false,
    has_previous: false
  });
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);
  const [organizationsPagination, setOrganizationsPagination] = useState({
    total: 0,
    total_pages: 1,
    current_page: 1,
    limit: 5,
    has_next: false,
    has_previous: false
  });
  const [organizationsCurrentPage, setOrganizationsCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const organizationsPerPage = 5;
  const allLoaded = !loadingUsers && !loadingOrganizations && !loadingRoles;
  const [showBottomCards, setShowBottomCards] = useState(false);

  // Delay bottom cards appearance by 200ms after users card appears (and all data loaded)
  useEffect(() => {
    let timeoutId;
    if (allLoaded) {
      setShowBottomCards(false);
      timeoutId = setTimeout(() => setShowBottomCards(true), 200);
    } else {
      setShowBottomCards(false);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [allLoaded]);

  // Fetch users and roles on component mount
  useEffect(() => {
    fetchUsers(1);
    fetchUserAccessRoles();
    fetchOrganizations(1);
    fetchIncubationFunctionalities();
  }, []);

  const fetchIncubationFunctionalities = async () => {
    try {
      const response = await getAllFunctionalities();
      if (response?.data?.success) {
        const incs = (response.data.functionalities || [])
          .filter(f => f.category === 'incubation');
        setIncubationFunctionalities(incs);
      }
    } catch (error) {
      console.error('Failed to fetch functionalities:', error);
    }
  };

  const fetchOrganizations = async (page = 1) => {
    try {
      setLoadingOrganizations(true);
      const response = await getOrganizations({ page: page, limit: organizationsPerPage });
      
      if (response.data && response.data.success) {
        setOrganizations(response.data.organizations || []);
        setOrganizationsPagination(response.data.pagination || {});
      } else {
        console.error('Failed to fetch organizations:', response.data?.error);
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      setLoadingUsers(true);
      const response = await getUsers({ page: page, limit: itemsPerPage });

      if (response.data && response.data.success) {
        const pageUsers = response.data.users || [];
        setUsers(pageUsers);
        setUsersPagination(response.data.pagination || {});

        const countEntries = await Promise.all(
          pageUsers.map(async (u) => {
            try {
              const res = await getUserIncubationAccess({ user_profile_id: String(u.id) });
              if (res?.data?.success && res.data.incubation_access) {
                return [u.id, Object.keys(res.data.incubation_access).length];
              }
            } catch (_) { /* swallow per-user failures */ }
            return [u.id, 0];
          })
        );
        setUserIncubationCounts(Object.fromEntries(countEntries));
      } else {
        console.error('Failed to fetch users:', response.data?.error);
        setUsers([]);
        setUserIncubationCounts({});
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setUserIncubationCounts({});
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserAccessRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await getUserAccessRoles();
      
      if (response.data && response.data.success) {
        // Separate active and archived roles
        const activeRoles = (response.data.roles || []).filter(role => !role.is_archived);
        const archivedRolesList = (response.data.roles || []).filter(role => role.is_archived);
        
        setUserAccessRoles(activeRoles);
        setArchivedRoles(archivedRolesList);
      } else if (response.data && response.data.error) {
        console.error('Failed to fetch roles:', response.data.error);
        // Set empty array on error to avoid breaking the UI
        setUserAccessRoles([]);
        setArchivedRoles([]);
      } else {
        console.error('Unexpected response format:', response);
        setUserAccessRoles([]);
        setArchivedRoles([]);
      }
    } catch (error) {
      console.error('Error fetching user access roles:', error);
      setUserAccessRoles([]);
      setArchivedRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handlePreviousPage = () => {
    const newPage = Math.max(currentPage - 1, 1);
    setCurrentPage(newPage);
    fetchUsers(newPage);
  };

  const handleNextPage = () => {
    const newPage = Math.min(currentPage + 1, usersPagination.total_pages);
    setCurrentPage(newPage);
    fetchUsers(newPage);
  };

  const handleOrganizationsPreviousPage = () => {
    const newPage = Math.max(organizationsCurrentPage - 1, 1);
    setOrganizationsCurrentPage(newPage);
    fetchOrganizations(newPage);
  };

  const handleOrganizationsNextPage = () => {
    const newPage = Math.min(organizationsCurrentPage + 1, organizationsPagination.total_pages);
    setOrganizationsCurrentPage(newPage);
    console.log('newPage', newPage);
    fetchOrganizations(newPage);
  };

  const openCreateRoleModal = () => {
    setRoleModal({
      mode: 'create',
      name: '',
      funcIds: [],
      initialName: '',
      initialFuncIds: []
    });
  };

  const openEditRoleModal = (role) => {
    const funcIds = (role.default_incubation_functionality_ids || []).map(String);
    setRoleModal({
      mode: 'edit',
      roleId: role.id,
      name: role.name || '',
      funcIds,
      initialName: role.name || '',
      initialFuncIds: funcIds
    });
  };

  const closeRoleModal = () => {
    if (savingRole) return;
    setRoleModal(null);
  };

  const handleModalNameChange = (value) => {
    setRoleModal(prev => prev ? { ...prev, name: value } : prev);
  };

  const toggleModalFuncId = (funcId, checked) => {
    setRoleModal(prev => {
      if (!prev) return prev;
      const set = new Set(prev.funcIds.map(String));
      if (checked) set.add(String(funcId));
      else set.delete(String(funcId));
      return { ...prev, funcIds: [...set] };
    });
  };

  const isRoleModalDirty = () => {
    if (!roleModal) return false;
    if (roleModal.name.trim() !== roleModal.initialName.trim()) return true;
    const a = [...roleModal.funcIds].map(String).sort();
    const b = [...roleModal.initialFuncIds].map(String).sort();
    if (a.length !== b.length) return true;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
    return false;
  };

  const handleSaveRole = async () => {
    if (!roleModal) return;
    const trimmedName = roleModal.name.trim();
    if (!trimmedName) {
      showWarning('Please enter a role name');
      return;
    }

    setSavingRole(true);
    try {
      const payload = {
        name: trimmedName,
        default_incubation_functionality_ids: roleModal.funcIds
      };
      const response = roleModal.mode === 'edit'
        ? await updateUserAccessRole({ role_id: roleModal.roleId, ...payload })
        : await createUserAccessRole(payload);

      if (response?.data?.success) {
        await fetchUserAccessRoles();
        setRoleModal(null);
        showSuccess(roleModal.mode === 'edit' ? 'Role updated' : 'Role created');
      } else {
        const errMsg = response?.data?.error || 'Unexpected response from server.';
        showError(roleModal.mode === 'edit' ? `Failed to update role: ${errMsg}` : `Failed to create role: ${errMsg}`);
      }
    } catch (error) {
      console.error('Failed to save role:', error);
      showError('Failed to save role. Please try again.');
    } finally {
      setSavingRole(false);
    }
  };

  const handleEditClick = () => {
    const newEditMode = !isEditMode;
    setIsEditMode(newEditMode);
    console.log('Edit mode changed to:', newEditMode);
  };

  const handleArchiveRole = async (role) => {
    try {
      setArchivingRoleId(role.id); // Set the role ID being archived
      const response = await toggleUserAccessRoleArchive({ role_id: role.id });
      if (response.data && response.data.success) {
        await fetchUserAccessRoles(); // Refresh roles to show updated archived status
      } else if (response.data && response.data.error) {
        console.error('Failed to archive role:', response.data.error);
        showError(`Failed to archive role: ${response.data.error}`);
      } else {
        console.error('Unexpected response format for archiving:', response);
        showError('Failed to archive role. Unexpected response from server.');
      }
    } catch (error) {
      console.error('Error archiving role:', error);
      showError('Failed to archive role. Please try again.');
    } finally {
      setArchivingRoleId(null); // Clear the loading state
    }
  };

  const handleUserRowClick = (userId) => {
    navigate(`/edit-user/${userId}`);
  };

  return (
    <div className="dashboard-access-container">
      <div className="dashboard-access-content">
        {allLoaded && (
        <Card className="dashboard-access-chart-section dashboard-access-card">
          <div className="card-header">
            <h2 className="dashboard-access-chart-title">Dashboard Users</h2>
            <Button 
              variant="primary" 
              size="small"
              onClick={() => navigate('/add-user')}
            >
              <i className="fas fa-plus-circle"></i>
              Add User
            </Button>
          </div>
          <div className="dashboard-access-table-container">
            {loadingUsers ? (
              <div>
                {/* Skeleton table header */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 3fr 1fr 1fr', gap: '16px', padding: '12px 16px' }}>
                  <Skeleton height={16} width="60%" />
                  <Skeleton height={16} width="60%" />
                  <Skeleton height={16} width="60%" />
                  <Skeleton height={16} width="40%" />
                  <Skeleton height={16} width="40%" />
                </div>
                {/* Skeleton rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '0 16px 16px 16px' }}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="skeleton-row">
                      <Skeleton className="skeleton-cell" width="80%" />
                      <Skeleton className="skeleton-cell" width="70%" />
                      <Skeleton className="skeleton-cell" width="90%" />
                      <Skeleton className="skeleton-cell" width="50%" />
                      <Skeleton className="skeleton-cell" width="50%" />
                    </div>
                  ))}
                </div>
              </div>
            ) : users.length > 0 ? (
              <table className="dashboard-access-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Organization</th>
                    <th>Role</th>
                    <th>Incubations</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr
                      key={user.id || index}
                      onClick={() => handleUserRowClick(user.id)}
                      className="dashboard-access-user-row"
                    >
                      <td>{`${user.first_name} ${user.last_name}`}</td>
                      <td>{user.email}</td>
                      <td>{user.organization?.name || user.organization}</td>
                      <td>
                        <span className={`dashboard-access-role-badge ${user.user_access_role?.name?.toLowerCase() === 'admin' ? 'admin' : user.user_access_role?.name?.toLowerCase() === 'csr' ? 'csr' : 'viewer'}`}>
                          {user.user_access_role?.name || 'No Role'}
                        </span>
                      </td>
                      <td className="dashboard-access-incubations-cell">
                        <span className="dashboard-access-incubations-count">
                          {userIncubationCounts[user.id] ?? 0}
                        </span>
                        <i className="fas fa-arrow-circle-right dashboard-access-incubations-arrow"></i>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="dashboard-access-no-users">
                <span>No users found</span>
              </div>
            )}
          </div>
          <div className="dashboard-access-pagination-container">
            <div className="dashboard-access-pagination-controls">
              <button 
                className="dashboard-access-pagination-button" 
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="dashboard-access-pagination-info">
                Page {currentPage} of {usersPagination.total_pages}
              </span>
              <button 
                className="dashboard-access-pagination-button" 
                onClick={handleNextPage}
                disabled={currentPage === usersPagination.total_pages}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </Card>
        )}
        
        {/* Organizations and User Access Roles Cards */}
        {allLoaded && showBottomCards && (
        <div className="dashboard-access-bottom-cards-container">
          <Card className="dashboard-access-chart-section dashboard-access-organization-card">
            <div className="card-header">
              <h2 className="dashboard-access-chart-title">Organizations</h2>
              <Button 
                variant="primary" 
                size="small"
                onClick={() => navigate('/add-organization')}
              >
                <i className="fas fa-plus-circle"></i>
                Add Organization
              </Button>
            </div>
            <div className="dashboard-access-organization-table-container">
              {loadingOrganizations ? (
                <div>
                  {/* Skeleton table header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr', gap: '16px', padding: '12px 16px' }}>
                    <Skeleton height={16} width="60%" />
                    <Skeleton height={16} width="40%" />
                    <Skeleton height={16} width="40%" />
                    <Skeleton height={16} width="40%" />
                  </div>
                  {/* Skeleton rows */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '0 16px 16px 16px' }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr', gap: '16px', alignItems: 'center', width: '100%' }}>
                        <Skeleton height={14} width="80%" />
                        <Skeleton height={14} width="60%" />
                        <Skeleton height={14} width="40%" />
                        <Skeleton height={14} width="50%" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : organizations.length > 0 ? (
                <table className="dashboard-access-organization-table">
                  <thead>
                    <tr>
                      <th>Organization Name</th>
                      <th>Type</th>
                      <th>Users</th>
                      <th>Incubations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizations.map((org) => (
                      <tr 
                        key={org.id}
                        className="dashboard-access-organization-row"
                        onClick={() => navigate(`/edit-organization/${org.id}`, { state: { mode: 'edit' } })}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{org.name}</td>
                        <td>{formatDisplayText(org.type)}</td>
                        <td>0</td>
                        <td className="dashboard-access-incubations-cell">
                          <span className="dashboard-access-incubations-count">0</span>
                          <i className="fas fa-arrow-circle-right dashboard-access-incubations-arrow"></i>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="dashboard-access-no-organizations">
                  <span>No organizations found</span>
                </div>
              )}
            </div>
            
            <div className="dashboard-access-pagination-container">
              <div className="dashboard-access-pagination-controls">
                <button 
                  className="dashboard-access-pagination-button" 
                  onClick={handleOrganizationsPreviousPage}
                  disabled={organizationsCurrentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span className="dashboard-access-pagination-info">
                  Page {organizationsCurrentPage} of {organizationsPagination.total_pages}
                </span>
                <button 
                  className="dashboard-access-pagination-button" 
                  onClick={handleOrganizationsNextPage}
                  disabled={organizationsCurrentPage === organizationsPagination.total_pages}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </Card>
          
                          <Card className="dashboard-access-chart-section dashboard-access-user-access-card">
                  <div className="dashboard-access-user-access-card-header">
                    <div className="dashboard-access-user-access-heading">
                      <h2 className="dashboard-access-chart-title">User Access Roles</h2>
                      <p className="dashboard-access-user-access-subtitle">
                        Roles define the default permissions applied when a user is added to an incubation.
                      </p>
                    </div>
                    <div className="dashboard-access-card-actions">
                      <i className="fas fa-plus-circle dashboard-access-action-icon" onClick={openCreateRoleModal} title="Create role"></i>
                    </div>
                  </div>
                  
                  {/* Active Roles Section */}
                  <div className="dashboard-access-user-access-content">
                    
                    {loadingRoles ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 10px 16px 10px' }}>
                        {Array.from({ length: 4 }).map((_, i) => (
                          <div key={i} className="dashboard-access-role-item">
                            <div className="dashboard-access-role-header">
                              <Skeleton width={100} height={24} />
                              <Skeleton width={80} height={14} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : userAccessRoles.length > 0 ? (
                      userAccessRoles.map((role, index) => (
                        <div
                          key={role.id || index}
                          className={`dashboard-access-role-item${!isEditMode ? ' clickable' : ''}`}
                          style={index === userAccessRoles.length - 1 ? { paddingBottom: '0px' } : {}}
                          onClick={isEditMode ? undefined : () => openEditRoleModal(role)}
                        >
                          <div className="dashboard-access-role-header">
                            <span className={`dashboard-access-role-badge ${role.name?.toLowerCase() === 'admin' ? 'admin' : role.name?.toLowerCase() === 'csr' ? 'csr' : 'viewer'}`}>
                              {role.name}
                            </span>
                            <span className="dashboard-access-role-count">
                              {(role.default_incubation_functionality_ids || []).length} permissions
                            </span>
                            {isEditMode ? (
                              <Popover content="Archive" placement="top" compact={true}>
                                <i
                                  className={`fas ${archivingRoleId === role.id ? 'fa-spinner fa-spin' : 'fa-archive'} dashboard-access-delete-icon`}
                                  onClick={(e) => { e.stopPropagation(); handleArchiveRole(role); }}
                                  title={`Archive ${role.name} role`}
                                  style={{ cursor: archivingRoleId === role.id ? 'not-allowed' : 'pointer' }}
                                ></i>
                              </Popover>
                            ) : (
                              <i className="fas fa-arrow-circle-right dashboard-access-role-arrow"></i>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="dashboard-access-no-roles">
                        <span>No active roles found</span>
                      </div>
                    )}
                  </div>

                  {/* Archived Roles Section */}
                  {archivedRoles.length > 0 && (
                    <div className="dashboard-access-archived-section">
                      <div className="dashboard-access-section-header">
                        <h3 className="dashboard-access-section-title">Archived Roles</h3>
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() => setShowArchived(!showArchived)}
                          className="dashboard-access-toggle-archived"
                        >
                          {showArchived ? 'Hide' : 'Show'} Archived ({archivedRoles.length})
                        </Button>
                      </div>
                      
                      {showArchived && (
                        <div className="dashboard-access-user-access-content">
                          {archivedRoles.map((role, index) => (
                            <div 
                              key={role.id || index} 
                              className="dashboard-access-role-item dashboard-access-role-item-archived"
                            >
                              <div className="dashboard-access-role-header">
                                <span className={`dashboard-access-role-badge ${role.name?.toLowerCase() === 'admin' ? 'admin' : role.name?.toLowerCase() === 'csr' ? 'csr' : 'viewer'} archived`}>
                                  {role.name}
                                </span>
                                <span className="dashboard-access-role-count">
                                  {(role.default_incubation_functionality_ids || []).length} permissions
                                </span>
                                    {isEditMode ? (
                                  <Popover content="Unarchive" placement="top" compact={true}>
                                    <i 
                                      className={`fas ${archivingRoleId === role.id ? 'fa-spinner fa-spin' : 'fa-archive'} dashboard-access-delete-icon`}
                                      onClick={() => handleArchiveRole(role)}
                                      title={`Unarchive ${role.name} role`}
                                      style={{ cursor: archivingRoleId === role.id ? 'not-allowed' : 'pointer' }}
                                    ></i>
                                  </Popover>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
          </Card>
        </div>
        )}
      </div>

      {roleModal && (
        <div className="role-modal-overlay" onClick={closeRoleModal} role="dialog" aria-modal="true">
          <div className="role-modal" onClick={(e) => e.stopPropagation()}>
            <div className="role-modal-header">
              <h3 className="role-modal-title">
                {roleModal.mode === 'edit' ? 'Edit Role' : 'Create Role'}
              </h3>
              <button
                type="button"
                className="role-modal-close"
                onClick={closeRoleModal}
                disabled={savingRole}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="role-modal-body">
              <div className="role-modal-field">
                <label className="role-modal-label">Role Name</label>
                <Input
                  type="text"
                  placeholder="Enter role name"
                  value={roleModal.name}
                  onChange={(e) => handleModalNameChange(e.target.value)}
                  className="role-modal-input"
                />
              </div>
              <div className="role-modal-field">
                <label className="role-modal-label">Default Incubation Permissions</label>
                <p className="role-modal-description">
                  These permissions will be pre-selected when a user with this role is added to a new incubation.
                </p>
                {incubationFunctionalities.length === 0 ? (
                  <p className="role-modal-empty">No incubation permissions available.</p>
                ) : (
                  <div className="role-modal-checklist">
                    {incubationFunctionalities.map(func => {
                      const idStr = String(func.id);
                      const checked = roleModal.funcIds.map(String).includes(idStr);
                      return (
                        <div key={func.id} className="role-modal-check-item">
                          <Checkbox
                            checked={checked}
                            onChange={(e) => toggleModalFuncId(idStr, e.target.checked)}
                            label={func.display_name}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="role-modal-actions">
              <Button variant="outline" size="medium" onClick={closeRoleModal} disabled={savingRole}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={handleSaveRole}
                disabled={
                  savingRole ||
                  !roleModal.name.trim() ||
                  (roleModal.mode === 'edit' && !isRoleModalDirty())
                }
              >
                {savingRole
                  ? (roleModal.mode === 'edit' ? 'Saving...' : 'Creating...')
                  : (roleModal.mode === 'edit' ? 'Save Changes' : 'Create Role')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardAccess;